<template>
    <div class="card mt-1" style="min-height: calc(100vh - 158px)">
        <span class="font-medium-2">Analytics of Profit and Loss</span>

        <div class="mt-1 overflow-x-scroll">
            <table class="table table-sm table-bordered align-middle mb-0 profit-loss-table">
                <thead>
                <tr>
                    <th style="min-width: 250px;">Account Head</th>
                    <th v-for="p in columns" :key="p.key" class="text-right" style="min-width: 100px;">
                        {{ p.label }}
                    </th>
                    <th class="text-right" style="min-width: 120px;">Total</th>
                </tr>
                </thead>

                <tbody>
                <template v-for="(section, sIndex) in sections" :key="sIndex">
                    <tr class="section-header clear-hover">
                        <td colspan="100%" class="py-2 border-0">
                            {{ section.title.toUpperCase() }}
                        </td>
                    </tr>

                    <tr
                        v-for="(row, i) in flattenNodes(section.data)"
                        :key="`${sIndex}-${i}`"
                        :data-depth="row.depth"
                        :class="{ 'bold-row': row.type === 'group' || row.isTotal }"
                    >
                        <td>
                            <span
                                class="pl-name d-inline-block"
                                :style="{ marginLeft: ((row.depth) * 18) + 'px' }"
                            >
                                {{ row.isTotal ? 'Total ' + row.name : row.name }}
                            </span>
                        </td>
                        <td v-for="p in columns" :key="p.key" class="text-right">
                            {{ row.type === 'group' ? '' : commaFormat((row.columnTotals[p.key] || 0), noOfDecimalsSafe) }}
                        </td>
                        <td class="text-right">
                            {{ row.type === 'group' ? '' : commaFormat(row.total, noOfDecimalsSafe) }}
                        </td>
                    </tr>

                    <tr class="bg-light bold-row">
                        <td>Total {{ section.title }}</td>
                        <td v-for="p in columns" :key="p.key" class="text-right">
                            {{ commaFormat(getSectionColumnTotal(section.data, p.key), noOfDecimalsSafe) }}
                        </td>
                        <td class="text-right">
                            {{ commaFormat(getSectionTotal(section.data), noOfDecimalsSafe) }}
                        </td>
                    </tr>

                    <!-- === GROSS PROFIT === -->
                    <template v-if="section.key === 'cost_of_sales'">
                        <tr class="bg-gp bold-row clear-hover">
                            <td>GROSS PROFIT (GP)</td>
                            <td v-for="p in columns" :key="p.key" class="text-right">
                                {{ commaFormat(((columnGrossProfit && columnGrossProfit[p.key]) || 0), noOfDecimalsSafe) }}
                            </td>
                            <td class="text-right">
                                {{ commaFormat(grossProfit, noOfDecimalsSafe) }}
                            </td>
                        </tr>
                        <tr class="bg-gp bold-row clear-hover">
                            <td>GROSS PROFIT (%)</td>
                            <td v-for="p in columns" :key="p.key" class="text-right">
                                {{ formatPercent((columnGrossProfit && columnGrossProfit[p.key]), (totalRevenueColumns && totalRevenueColumns[p.key])) }}
                            </td>
                            <td class="text-right">
                                {{ formatPercent(grossProfit, totalRevenue) }}
                            </td>
                        </tr>
                    </template>

                    <!-- === OPERATING PROFIT === -->
                    <template v-if="section.key === 'operating_expenses'">
                        <tr class="bg-op bold-row clear-hover">
                            <td>OPERATING PROFIT / (LOSS)</td>
                            <td v-for="p in columns" :key="p.key" class="text-right">
                                {{ commaFormat(((columnOperatingProfit && columnOperatingProfit[p.key]) || 0), noOfDecimalsSafe) }}
                            </td>
                            <td class="text-right">
                                {{ commaFormat(operatingProfit, noOfDecimalsSafe) }}
                            </td>
                        </tr>
                        <tr class="bg-op bold-row clear-hover">
                            <td>OPERATING PROFIT (%)</td>
                            <td v-for="p in columns" :key="p.key" class="text-right">
                                {{ formatPercent((columnOperatingProfit && columnOperatingProfit[p.key]), (totalRevenueColumns && totalRevenueColumns[p.key])) }}
                            </td>
                            <td class="text-right">
                                {{ formatPercent(operatingProfit, totalRevenue) }}
                            </td>
                        </tr>
                    </template>

                    <!-- === NET PROFIT === -->
                    <template v-if="section.key === 'other_expense'">
                        <tr class="bg-np bold-row clear-hover">
                            <td>NET PROFIT / (LOSS)</td>
                            <td v-for="p in columns" :key="p.key" class="text-right">
                                {{ commaFormat(((columnNetProfit && columnNetProfit[p.key]) || 0), noOfDecimalsSafe) }}
                            </td>
                            <td class="text-right">
                                {{ commaFormat(netProfit, noOfDecimalsSafe) }}
                            </td>
                        </tr>
                        <tr class="bg-np bold-row clear-hover">
                            <td>NET PROFIT (%)</td>
                            <td v-for="p in columns" :key="p.key" class="text-right">
                                {{ formatPercent((columnNetProfit && columnNetProfit[p.key]), (totalRevenueColumns && totalRevenueColumns[p.key])) }}
                            </td>
                            <td class="text-right">
                                {{ formatPercent(netProfit, totalRevenue) }}
                            </td>
                        </tr>
                    </template>
                </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import figureFormatter from "@/services/utils/figureFormatter";

export default {
    name: "ProfitLossAnalyticsTable",
    props: {
        sections: { type: Array, required: true },
        columns: { type: Array, default: () => [] },
        noOfDecimals: { type: [Number, String], default: 2 },
        grossProfit: { type: Number, default: 0 },
        operatingProfit: { type: Number, default: 0 },
        netProfit: { type: Number, default: 0 },
        columnGrossProfit: { type: Object, default: () => ({}) },
        columnOperatingProfit: { type: Object, default: () => ({}) },
        columnNetProfit: { type: Object, default: () => ({}) }
    },

    setup() {
        const { commaFormat } = figureFormatter();
        return { commaFormat };
    },

    computed: {
        noOfDecimalsSafe() {
            const val = parseInt(this.noOfDecimals);
            return isNaN(val) || val < 0 ? 2 : val;
        },

        totalRevenue() {
            const revenueSection = this.sections.find(
                (s) => s && s.title && s.title.toLowerCase() === "revenue"
            );
            if (!revenueSection) return 0;
            return this.getSectionTotal(revenueSection.data);
        },

        totalRevenueColumns() {
            const revenueSection = this.sections.find(
                (s) => s && s.title && s.title.toLowerCase() === "revenue"
            );
            const map = {};
            if (!revenueSection) return map;
            this.columns.forEach((p) => {
                map[p.key] = this.getSectionColumnTotal(revenueSection.data, p.key);
            });
            return map;
        }
    },

    methods: {
        computeColumn(node, key) {
            if (!node) return 0;
            if (node.type === "endpoint") {
                return (node.columns && node.columns[key]) || 0;
            }
            if (Array.isArray(node.child) && node.child.length) {
                return node.child.reduce((sum, c) => sum + this.computeColumn(c, key), 0);
            }
            return 0;
        },

        flattenNodes(nodes, depth = 1) {
            if (!Array.isArray(nodes)) nodes = nodes ? [nodes] : [];
            let rows = [];
            nodes.forEach((node) => {
                if (!node) return;

                const columnTotals = {};
                this.columns.forEach((p) => {
                    columnTotals[p.key] = this.computeColumn(node, p.key);
                });
                const total = Object.values(columnTotals).reduce((a, b) => a + b, 0);

                rows.push({
                    name: node.name,
                    type: node.type,
                    depth,
                    total,
                    columnTotals,
                    isTotal: false
                });

                if (Array.isArray(node.child) && node.child.length) {
                    rows = rows.concat(this.flattenNodes(node.child, depth + 1));

                    const totalColumn = {};
                    this.columns.forEach((p) => {
                        totalColumn[p.key] = this.computeColumn(node, p.key);
                    });
                    const totalSum = Object.values(totalColumn).reduce((a, b) => a + b, 0);

                    rows.push({
                        name: node.name,
                        type: "total",
                        depth,
                        total: totalSum,
                        columnTotals: totalColumn,
                        isTotal: true
                    });
                }
            });
            return rows;
        },

        getSectionColumnTotal(nodes, key) {
            if (!Array.isArray(nodes)) return 0;
            return nodes.reduce((sum, n) => sum + this.computeColumn(n, key), 0);
        },

        getSectionTotal(nodes) {
            if (!Array.isArray(nodes)) return 0;
            const columnTotals = {};
            this.columns.forEach((p) => {
                columnTotals[p.key] = this.getSectionColumnTotal(nodes, p.key);
            });
            return Object.values(columnTotals).reduce((a, b) => a + b, 0);
        },

        formatPercent(value, total) {
            if (!value || !total || total === 0) return "-";
            return ((value / total) * 100).toFixed(2) + "%";
        }
    }
};
</script>

<style scoped>
td, th {
    white-space: nowrap;
}
thead th {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: #f8f9fa;
    font-weight: 600;
    padding-top: 6px;
    padding-bottom: 6px;
}
.pl-name {
    vertical-align: middle;
}
.bold-row td,
.section-header td {
    font-weight: 700 !important;
}
tr.clear-hover:hover {
    --bs-table-accent-bg: transparent;
}
.bg-gp { background-color: #dff0d8 !important; }
.bg-op { background-color: #d9edf7 !important; }
.bg-np { background-color: #fbeed5 !important; }
</style>
